import { Component } from '@theme/component';

/**
 * Frelan: "About the collection" (sections/collection-about.liquid) — the collection
 * description after the products, cut to a few lines with a "Read more" button.
 *
 * The text is always fully in the page (CSS only clips it), so search engines read all of it.
 * The button only appears when the text is taller than the clamp: a short description shows
 * in full with no button. Re-measured whenever the text box resizes, because the clamp is in
 * lines and how much fits changes with the width.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} content
 * @property {HTMLButtonElement} toggle
 *
 * @extends {Component<Refs>}
 */
class CollectionAbout extends Component {
  requiredRefs = ['content', 'toggle'];

  /** @type {ResizeObserver | undefined} */
  #observer;

  /** @type {Animation | undefined} */
  #animation;

  connectedCallback() {
    super.connectedCallback();
    this.#observer = new ResizeObserver(this.#measure);
    this.#observer.observe(this.refs.content);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
  }

  get #expanded() {
    return this.dataset.expanded === 'true';
  }

  /** Shows the button (and the fade) only when the clamp is hiding some of the text. */
  #measure = () => {
    if (this.#expanded) return;

    const { content, toggle } = this.refs;
    const clipped = content.scrollHeight > content.clientHeight + 1;

    toggle.hidden = !clipped;
    this.dataset.clipped = String(clipped);
  };

  toggleText() {
    const { content, toggle } = this.refs;
    const expanded = !this.#expanded;
    const startHeight = content.offsetHeight;

    this.dataset.expanded = String(expanded);
    toggle.setAttribute('aria-expanded', String(expanded));

    const endHeight = content.offsetHeight;

    // Closing from far down the text: bring the start of it back into view.
    if (!expanded && this.getBoundingClientRect().top < 0) {
      this.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#animation?.cancel();
    this.#animation = content.animate(
      [{ height: `${startHeight}px` }, { height: `${endHeight}px` }],
      { duration: 300, easing: 'ease' }
    );
  }
}

if (!customElements.get('collection-about')) {
  customElements.define('collection-about', CollectionAbout);
}
